/**
 * The adjustable heights of the schedule creator's stacked panels: the
 * schedule window itself, the "Drag to assign" crew list and the client
 * feedback list.
 *
 * Storage and clamping come from the shared `scheduleDimension` module (the
 * same one behind the label-column width); this file only declares what is
 * specific to panels — their range, their keys, and their defaults.
 */

import { clampDimension, type ScheduleDimension } from "@/lib/scheduleDimension";

export const MIN_PANEL_HEIGHT = 120;
export const MAX_PANEL_HEIGHT = 2000;

export const PANEL_HEIGHT_DIMENSION: ScheduleDimension = {
  keyPrefix: "schedulePanelHeight",
  min: MIN_PANEL_HEIGHT,
  max: MAX_PANEL_HEIGHT,
};

/** Stable reference so the resize handle's clamp prop doesn't change identity. */
export const clampPanelHeight = (height: number): number =>
  clampDimension(PANEL_HEIGHT_DIMENSION, height);

/** Panel keys. Values double as the localStorage scope segment. */
export const PANEL_TIMELINE = "timeline";
export const PANEL_CREW_LIST = "crewList";
export const PANEL_FEEDBACK = "feedback";

/** Default height of the "Drag to assign" crew list. */
export const DEFAULT_CREW_LIST_HEIGHT = 240;
/** Default max height of the client-feedback list (was 24rem). */
export const DEFAULT_FEEDBACK_HEIGHT = 384;

/** Chrome above the schedule window (header, day picker, filter bar, …). */
const TIMELINE_VIEWPORT_OFFSET = 380;
/** Floor for the viewport-derived default, mirroring the old min-h-[400px]. */
const TIMELINE_MIN_DEFAULT_HEIGHT = 400;

/**
 * Default height of the schedule window: what the old
 * `h-[calc(100vh-380px)] min-h-[400px]` produced, so an untouched schedule
 * fills the browser exactly as before. Takes the viewport height as an
 * argument so it stays a pure function for tests.
 */
export function defaultTimelineHeight(viewportHeight = globalThis.innerHeight ?? 0): number {
  if (!Number.isFinite(viewportHeight)) return TIMELINE_MIN_DEFAULT_HEIGHT;
  return Math.max(
    TIMELINE_MIN_DEFAULT_HEIGHT,
    Math.round(viewportHeight - TIMELINE_VIEWPORT_OFFSET),
  );
}
